import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';

const SUPPORT_EMAIL = 'support@coachsightecr.com';
const SUPPORT_PHONE = '(919) 627-7409';

function getLoggedInUser(): { name: string; email: string; userId: string } {
  try {
    if (!localStorage.getItem('authToken')) return { name: '', email: '', userId: '' };
    const data = JSON.parse(localStorage.getItem('userData') || '{}');
    const name = [data.firstName, data.lastName].filter(Boolean).join(' ');
    return { name, email: data.email || '', userId: data.id || '' };
  } catch {
    return { name: '', email: '', userId: '' };
  }
}

type IssueType = 'bug' | 'feature_request' | 'question';

export default function SupportPage() {
  const prefill = getLoggedInUser();

  const [form, setForm] = useState({
    subject: '',
    issue_type: '' as IssueType | '',
    description: '',
    reporter_name: prefill.name,
    reporter_email: prefill.email,
    reporter_phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl.replace('/api', '')}/support/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          user_id: prefill.userId || undefined,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Submission failed. Please try again.');
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900">CoachSight</span>
            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">ECR</span>
          </Link>
          <Link to="/login" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">
            Sign In
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-10">
        {/* Left — contact info */}
        <div className="lg:col-span-1 space-y-6">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 mb-1">Support</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              Having trouble or have a suggestion? We're here to help.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Contact Us</p>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <EnvelopeIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Email</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <PhoneIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                <a
                  href={`tel:${SUPPORT_PHONE.replace(/\D/g, '')}`}
                  className="text-sm font-medium text-gray-800 hover:text-blue-600"
                >
                  {SUPPORT_PHONE}
                </a>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5">
            <p className="text-xs font-semibold text-blue-700 mb-1">Response time</p>
            <p className="text-sm text-blue-600 leading-relaxed">
              We typically respond within one business day. For urgent issues, please call.
            </p>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Report submitted</h2>
                <p className="text-gray-500 text-sm mb-6">
                  Thank you — we'll follow up at the email you provided.
                </p>
                <button
                  onClick={() => { setSuccess(false); setForm(f => ({ ...f, subject: '', description: '', issue_type: '' })); }}
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  Submit another report
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-6">Report an Issue</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      className={inputClass}
                      placeholder="Brief summary of the issue"
                    />
                  </div>

                  {/* Issue type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={form.issue_type}
                      onChange={e => setForm(f => ({ ...f, issue_type: e.target.value as IssueType }))}
                      className={inputClass}
                    >
                      <option value="">Select a type…</option>
                      <option value="bug">Bug</option>
                      <option value="feature_request">Feature Request</option>
                      <option value="question">Question</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      className={inputClass}
                      placeholder="Please describe the issue in as much detail as possible…"
                    />
                  </div>

                  {/* Reporter info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text"
                        value={form.reporter_name}
                        onChange={e => setForm(f => ({ ...f, reporter_name: e.target.value }))}
                        className={inputClass}
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={form.reporter_email}
                        onChange={e => setForm(f => ({ ...f, reporter_email: e.target.value }))}
                        className={inputClass}
                        placeholder="jane@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      value={form.reporter_phone}
                      onChange={e => setForm(f => ({ ...f, reporter_phone: e.target.value }))}
                      className={inputClass}
                      placeholder="+1 555 000 0000"
                    />
                  </div>

                  {error && <p className="text-red-600 text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 text-sm"
                  >
                    {submitting ? 'Submitting…' : 'Submit Report'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
